import { html, css, nothing, type TemplateResult } from 'lit';
import { customElement } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';

import NSAutoPaymentsCard from './card-base.js';
import JobSchema from '../../../../../models/job-schema.js';
import definition from '@job-schemas/public-ledger/auto-payments/limit-reached.json';
import { jobSchema } from '../../../job-schema.js';
import { AUTOPAY_CARD_OUTCOMES } from './presentations.js';
import { formatPence } from '../../../../../helpers/credit-pricing.js';
import { autopayCardAction } from '../../../../../helpers/auto-payments-api.js';
import { CHANGE_STARTING, CHANGE_START_FAILED } from '../../../../../helpers/auto-payment-changes.js';
import {
  canCloseForMonth,
  limitReachedActions,
  limitReachedFigures,
  limitReachedText,
  monthLabel,
  type LimitReachedAction,
} from '../../../../../helpers/auto-payment-cards.js';

import '../../../../../ns-elements/ns-job-layout.js';
import '../../../../../ns-elements/ns-detail-list.js';

/**
 * Automatic payments monthly limit reached (#25 phase 4, DECISION 2.3, 2.4, 3.4): the card limit (succeeded charges less
 * refunds) or the operating top-up limit is reached for a UK calendar month, so that kind of automatic payment has stopped
 * until the month ends. One card per company, limit and month, claimed by a signatory, who closes it for the month
 * (public-ledger/autopay/card-action close-for-month). The sweep ends it `month-ended` once the month is over. Raise (#25
 * phase 6) starts Change automatic payments amounts as this card's child, for the signatory who pressed it; an applied
 * change lifts this month's stop for each limit it raises, and the card closes when the change ends.
 * A signed stop (#40) closes it `stopped` (public-ledger/autopay/stop-finish).
 */
@customElement(NSPublicLedgerAutoPaymentsLimitReached.is)
export default class NSPublicLedgerAutoPaymentsLimitReached extends NSAutoPaymentsCard {
  static get is() {
    return 'ns-public-ledger-auto-payments-limit-reached';
  }

  static get schema(): JobSchema {
    // Every outcome is hidden: presentations.ts says whose each one is
    return jobSchema(definition, {
      outcomes: AUTOPAY_CARD_OUTCOMES['@ns/public-ledger-auto-payments-limit-reached'],
      icon: 'bank-card',
    });
  }

  static styles = [
    ...super.styles,
    css`
      .job-panel ns-detail-list {
        --ns-detail-list-gap: var(--wa-space-2xs);
      }
    `,
  ];

  private get __figures() {
    return limitReachedFigures(this._job?.metadata);
  }

  private async __closeForMonth() {
    const job = this._job;
    if (!job) return;
    await this._serverAction(
      async () => {
        await autopayCardAction(this._auth?.token, job.id, 'close-for-month');
        return 'Closed for the month. This job closes in a moment.';
      },
      'Closing for the month',
      'This could not be closed for the month. Please try again.',
    );
  }

  private async __raise() {
    await this._startChange('raise', CHANGE_STARTING, CHANGE_START_FAILED);
  }

  render() {
    const figures = this.__figures;
    const card = figures.limit !== 'OPERATING';
    const monthOver = this._claimed && !this._completed && !canCloseForMonth(figures);
    return html`
      <ns-job-layout>
        <p slot="intro">A monthly limit on your company’s automatic payments has been reached.</p>
        ${this._renderBlocked()}
        ${this._renderWaiting() ??
        (this._claimed || this._completed
          ? html`
              <div class="job-panel">
                <h3>${card ? 'Monthly card limit' : 'Monthly operating top-up limit'}</h3>
                <p>${limitReachedText(figures)}</p>
                ${monthOver
                  ? html`
                      <p>That month is over, so the limit has lifted. This job closes by itself shortly.</p>
                    `
                  : html`
                      <p>
                        Until then, public ledger actions your company’s accounts can’t pay for are refused. Press Close
                        for the month to confirm you have seen this.
                      </p>
                    `}
              </div>
            `
          : this._renderClaimNote('close the month'))}
        <div class="job-panel">
          <h3>This month</h3>
          <ns-detail-list
            .rows=${[
              { label: 'Month', value: monthLabel(figures.month) ?? undefined },
              { label: 'Limit', value: figures.limitPence !== null ? formatPence(figures.limitPence) : undefined },
              {
                label: card ? 'Charged so far, less refunds' : 'Topped up so far',
                value: figures.usedPence !== null ? formatPence(figures.usedPence) : undefined,
              },
            ]}
          ></ns-detail-list>
        </div>
        <div slot="aside" class="primary-info">
          <span class="info-icon" aria-hidden="true">i</span>
          <h3>Monthly limits</h3>
          <p>
            The signatories set two monthly limits when they set up automatic payments: the most charged to the card,
            and the most moved to the operating account, each in a calendar month, UK time.
          </p>
          <p class="note">Each limit lifts by itself at the start of the next month.</p>
        </div>
      </ns-job-layout>
    `;
  }

  renderActions() {
    const actions = limitReachedActions({
      completed: this._completed || this._waiting !== null,
      claimed: this._claimed,
      canClose: canCloseForMonth(this.__figures),
    });
    const buttons: Record<LimitReachedAction, TemplateResult> = {
      CLAIM: this._renderClaimButton(),
      RAISE: html`
        <wa-button variant="brand" type="button" ?disabled=${this._busy} @click=${() => this.__raise()}>
          Raise
        </wa-button>
      `,
      CLOSE_FOR_MONTH: html`
        <wa-button
          variant="brand"
          appearance="outlined"
          type="button"
          ?disabled=${this._busy}
          @click=${() => this.__closeForMonth()}
        >
          Close for the month
        </wa-button>
      `,
    };
    return actions.length === 0
      ? nothing
      : html`
          ${actions.map((action) => buttons[action])}
        `;
  }
}
